import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ICONS } from '../constants';
import { API_CONFIG } from '../config';

interface ShotVideoPlayerProps {
  videoUrl: string;
  frameIn: number;
  frameOut: number;
  fps?: number;
  shotNumber: number;
  onClose: () => void;
  timecodeInSeconds?: number;
  timecodeOutSeconds?: number;
}

/** Cap preview window so HD sources don't fill the monitor */
const MAX_VIEWPORT_FRAC = 0.65;
const VIEWPORT_PAD_PX = 48;

/**
 * Minimal in/out loop preview over the bidding sheet.
 * Out is inclusive (start of last frame); exclusive end = out + 1/fps.
 * Display: native aspect, never upscale, max ~65% of viewport.
 */
const ShotVideoPlayer: React.FC<ShotVideoPlayerProps> = ({
  videoUrl,
  frameIn,
  frameOut,
  fps = 24,
  shotNumber: _shotNumber,
  onClose,
  timecodeInSeconds,
  timecodeOutSeconds,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const rafRef = useRef<number | null>(null);
  const wantLoopRef = useRef(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [displaySize, setDisplaySize] = useState<{ w: number; h: number } | null>(null);

  const startTime = useMemo(
    () =>
      timecodeInSeconds !== undefined
        ? Number(timecodeInSeconds)
        : (frameIn - 1001) / fps,
    [timecodeInSeconds, frameIn, fps]
  );

  const exclusiveEnd = useMemo(
    () =>
      timecodeOutSeconds !== undefined
        ? Number(timecodeOutSeconds) + 1 / fps
        : (frameOut - 1001 + 1) / fps,
    [timecodeOutSeconds, frameOut, fps]
  );

  const epsilon = 1 / (fps * 4);

  const getLoopEnd = useCallback(
    (video: HTMLVideoElement) => {
      const duration = Number.isFinite(video.duration) ? video.duration : exclusiveEnd;
      return Math.min(exclusiveEnd, duration);
    },
    [exclusiveEnd]
  );

  const updateDisplaySize = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) return;

    const maxW = Math.max(160, Math.min(window.innerWidth - VIEWPORT_PAD_PX, window.innerWidth * MAX_VIEWPORT_FRAC));
    const maxH = Math.max(90, Math.min(window.innerHeight - VIEWPORT_PAD_PX, window.innerHeight * MAX_VIEWPORT_FRAC));
    const scale = Math.min(1, maxW / video.videoWidth, maxH / video.videoHeight);

    setDisplaySize({
      w: Math.max(1, Math.round(video.videoWidth * scale)),
      h: Math.max(1, Math.round(video.videoHeight * scale)),
    });
  }, []);

  const stopLoopWatch = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const restartAtIn = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = startTime;
  }, [startTime]);

  const startLoopWatch = useCallback(() => {
    stopLoopWatch();
    const tick = () => {
      const video = videoRef.current;
      if (!video || video.paused || !wantLoopRef.current) {
        rafRef.current = null;
        return;
      }
      const loopEnd = getLoopEnd(video);
      if (video.currentTime >= loopEnd - epsilon) {
        video.currentTime = startTime;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [epsilon, getLoopEnd, startTime, stopLoopWatch]);

  const play = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    wantLoopRef.current = true;
    try {
      await video.play();
      setIsPlaying(true);
      startLoopWatch();
    } catch (err) {
      console.error('Error playing video:', err);
      setError('Не удалось воспроизвести видео');
      setIsPlaying(false);
      stopLoopWatch();
    }
  }, [startLoopWatch, stopLoopWatch]);

  const pause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    wantLoopRef.current = false;
    video.pause();
    setIsPlaying(false);
    stopLoopWatch();
  }, [stopLoopWatch]);

  const togglePlayPause = useCallback(() => {
    if (isPlaying) pause();
    else void play();
  }, [isPlaying, pause, play]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onMeta = () => updateDisplaySize();
    video.addEventListener('loadedmetadata', onMeta);
    video.addEventListener('loadeddata', onMeta);
    window.addEventListener('resize', updateDisplaySize);
    if (video.readyState >= 1) updateDisplaySize();

    return () => {
      video.removeEventListener('loadedmetadata', onMeta);
      video.removeEventListener('loadeddata', onMeta);
      window.removeEventListener('resize', updateDisplaySize);
    };
  }, [updateDisplaySize, videoUrl]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    wantLoopRef.current = true;

    const onLoaded = () => {
      updateDisplaySize();
      restartAtIn();
      void play();
    };

    const onEnded = () => {
      if (!wantLoopRef.current) {
        setIsPlaying(false);
        stopLoopWatch();
        return;
      }
      restartAtIn();
      void play();
    };

    video.addEventListener('ended', onEnded);

    if (video.readyState >= 1) {
      onLoaded();
    } else {
      video.addEventListener('loadedmetadata', onLoaded, { once: true });
    }

    return () => {
      video.removeEventListener('loadedmetadata', onLoaded);
      video.removeEventListener('ended', onEnded);
      wantLoopRef.current = false;
      stopLoopWatch();
      video.pause();
    };
  }, [startTime, exclusiveEnd, play, stopLoopWatch, restartAtIn, updateDisplaySize]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        togglePlayPause();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, togglePlayPause]);

  const cleanVideoUrl = videoUrl.replace(/^\/app/, '');
  const fullVideoUrl = cleanVideoUrl.startsWith('http')
    ? cleanVideoUrl
    : `${API_CONFIG.BASE_URL}${cleanVideoUrl}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-4 right-4 z-20 p-2 hover:bg-white/10 rounded-lg transition-colors"
        title="Закрыть (Esc)"
      >
        <ICONS.X className="w-7 h-7 text-white" />
      </button>

      {error ? (
        <div
          className="flex flex-col items-center justify-center px-8 text-center"
          onClick={(e) => e.stopPropagation()}
        >
          <ICONS.AlertCircle className="w-12 h-12 text-red-500 mb-3" />
          <p className="text-white text-sm">{error}</p>
        </div>
      ) : (
        <div
          className="relative flex-shrink-0 overflow-hidden rounded shadow-2xl bg-black"
          style={
            displaySize
              ? { width: displaySize.w, height: displaySize.h }
              : { width: 'min(65vw, 960px)', height: 'auto', maxHeight: '65vh' }
          }
          onClick={(e) => e.stopPropagation()}
        >
          <video
            ref={videoRef}
            src={fullVideoUrl}
            className="block cursor-pointer bg-black"
            style={{
              width: displaySize ? displaySize.w : '100%',
              height: displaySize ? displaySize.h : 'auto',
              maxWidth: '65vw',
              maxHeight: '65vh',
              objectFit: 'contain',
            }}
            width={displaySize?.w}
            height={displaySize?.h}
            playsInline
            onClick={(e) => {
              e.stopPropagation();
              togglePlayPause();
            }}
            onLoadedMetadata={updateDisplaySize}
            onError={() => setError('Не удалось загрузить видео')}
          />
        </div>
      )}
    </div>
  );
};

export default ShotVideoPlayer;
